import { NavigationItem } from "../types";
import { convertTimestampToIST } from "../utils/formatters";

// API Configuration
export const API_CONFIG = {
  BASE_URL: process.env.REACT_APP_API_BASE_URL,
  TIMEOUT: 300000, // 5 minutes
  RETRY_ATTEMPTS: 3,
};

/**
 * Public admin app origin for payment link `callback_url` etc.
 * CRA exposes only `REACT_APP_*` — set `REACT_APP_PAYMENT_CALLBACK_URL` in `.env` (no trailing slash).
 * Legacy `PAYMENT_CALLBACK_URL` is still read if present (custom builds).
 * In the browser, falls back to `window.location.origin` when unset (local dev).
 */
export function getAppPublicOrigin(): string {
  return String(process.env.REACT_APP_PAYMENT_CALLBACK_URL ?? "");
}

// API Endpoints
export const API_ENDPOINTS = {
  AUTH: {
    LOGIN: "/UserService/Login/GetUserLoginDetails",
    LOGOUT: "/UserService/Login/Logout",
    LOGIN_APP_VERSION: "5.1",
    REFRESH: "/auth/refresh",
    PROFILE: "/UserService/Login/GetUserLoginDetails",
  },
  USERS: {
    LIST: "/UserService/User/GetAllUsers",
    GETBYID: "/UserService/User/GetUserById",
    GETUSERBYUSERID: "/UserService/User/GetUserByUserId",
    GET_ROLE_HUB_RELATION_LIST:
      "/UserService/User/GetRoleHubRelationListByUserIdAsync",
    GETALLROLES: "/UserService/User/GetAllRoles",
    GET_KYC_BY_USER_ID: "/UserService/KYC/GetKYCByUserId",
    GET_KYC_BY_USER_ID_BOOKING: "/UserService/KYC/GetKYCByUserId",
    UPDATE_STATUS: "/UserService/User/UpdateUserStatus",
    FILTER_USER: "/UserService/User/FilterUser",
  },
  COMMON: {
    SAVE_IMAGE: "/CommonService/Common/SaveMedia",
    GET_STATES: "/CommonService/Common/GetStates",
    GET_CITIES: "/CommonService/Common/GetCities",
    GET_CITIES_BY_STATE: "/CommonService/Common/GetCitiesByState",
    GET_ALL_CODE_COMMON: "/CommonService/Common/GetAllCodeCommon",
    GET_ALL_CODE_COMMON_BY_CATEGORY:
      "/CommonService/Common/GetAllCodeCommonByCategory",
  },
  HUBS: {
    LIST: "/Hub_Vehicle_Staff/HubManagement/GetAllHubs",
    CREATE: "/Hub_Vehicle_Staff/HubManagement/CreateHub",
    GETBYID: "/Hub_Vehicle_Staff/HubManagement/GetHubById",
    UPDATE: `/Hub_Vehicle_Staff/HubManagement/UpdateHub`,
    DELETE: `/Hub_Vehicle_Staff/HubManagement/DeleteHub`,
    STATS: "/Hub_Vehicle_Staff/HubManagement/GetHubStats",
  },
  STAFF: {
    LIST: "/UserService/User/GetAllUsers",
    CREATE: "/UserService/User/CreateOrUpdateUser",
    UPDATE: (id: string) => `/UserService/User/CreateOrUpdateUser${id}`,
    DELETE: "/UserService/User/DeleteUser",
    GETBYID: "/UserService/User/GetUserById",
    STATS: "/UserService/Staff/GetStaffStats",
  },
  VEHICLES: {
    LIST: "/Hub_Vehicle_Staff/VehicleManagement/GetAllVehicles",
    GETBYHUBID: "/Hub_Vehicle_Staff/VehicleManagement/GetVehiclesByHubId",
    CREATE: "/Hub_Vehicle_Staff/VehicleManagement/CreateVehicle",
    GETBYID: "/Hub_Vehicle_Staff/VehicleManagement/GetVehicleById",
    UPDATE: (id: string) =>
      `/Hub_Vehicle_Staff/VehicleManagement/UpdateVehicle`,
    UPDATE_STATUS: (id: number, status: number) =>
      `/Hub_Vehicle_Staff/VehicleManagement/ChangeVehicleStatusToUnderbooking?vehicleid=${id}`,
    DELETE: (id: string) =>
      `/Hub_Vehicle_Staff/VehicleManagement/DeleteVehicle`,
    STATS: "/Hub_Vehicle_Staff/VehicleManagement/GetVehicleStats",
  },
  PLANS: {
    LIST: "/Hub_Vehicle_Staff/HubManagement/GetAllHubs",
    CREATE: "/Hub_Vehicle_Staff/Plan/CreatePlan",
    GETBYID: "/Hub_Vehicle_Staff/Plan/GetPlan",
    GETBYHUBID: "/Hub_Vehicle_Staff/Plan/GetPlansByHubId",
    UPDATE: `/Hub_Vehicle_Staff/Plan/UpdatePlan`,
    DELETE: (id: string) => `/Hub_Vehicle_Staff/Plan/DeletePlan?id=${id}`,
    STATS: "/Hub_Vehicle_Staff/Plan/GetPlanStats",
  },
  TEMPLATES: {
    LIST: "/Adminservice/Template/GetAllTemplates",
    CREATE: "/Adminservice/Template/AddTemplate",
    GETBYIDORNAME: "/Adminservice/Template/GetTemplateByIdOrName",
    UPDATE: "/AdminService/Template/UpdateTemplate",
    DELETE: "/Adminservice/Template/DeleteTemplate",
  },
  PAYMENTS: {
    LIST: "/Payment/GetAllPayments",
    CREATE: "/Payment/CreatePayment",
    UPDATE: (id: string) => `/Payment/UpdatePayment/${id}`,
    DELETE: (id: string) => `/Payment/DeletePayment/${id}`,
    STATS: "/Payment/GetPaymentStats",
    SEARCH: "/PaymentService/Payment/SearchPaymentDetails",
    GET_DETAILS: "/PaymentService/Payment/GetPaymentDetails",
    SAVE_DETAILS: "/PaymentService/Payment/SavePaymentDetails",
    CREATE_RAZORPAY_ORDER: "/PaymentService/Payment/createRazorPayOrderID",
    CREATE_PAYMENT_LINK: "/PaymentService/Payment/CreatePaymentLink",
    CREATE_QR_CODE: "/PaymentService/Payment/CreateQRCode",
    VERIFY_PAYMENT_BY_ORDER_ID:
      "/PaymentService/Payment/VerifyPaymentbyOrderID",
  },
  BOOKINGS: {
    LIST: "/Booking_Trip_ManagementService/Booking/GetAllBookings",
    CREATE: "/Booking_Trip_ManagementService/Booking/CreateBooking",
    UPDATE: (id: string) => `/Booking/UpdateBooking/${id}`,
    UPDATE_BOOKING: "/Booking_Trip_ManagementService/Booking/UpdateBooking",
    DELETE: (id: string) => `/Booking/DeleteBooking/${id}`,
    STATS: "/Booking/GetBookingStats",
    DETAILS: (id: string) =>
      `/Booking_Trip_ManagementService/Trip/GetTripByBookingId?bookingId=${id}`,
    GET_BY_USER_ID: (userId: string) =>
      `/Booking_Trip_ManagementService/Booking/GetBookingsByUserId/${userId}`,
    DELETE_BY_BOOKING_ID: (bookingId: string) =>
      `/Booking_Trip_ManagementService/Booking/DeleteBookingByBookingId/${bookingId}`,
    CANCEL_BOOKING: (bookingId: string, cancelReason: string) => {
      const q = new URLSearchParams();
      q.set("bookingid", String(bookingId));
      q.set("cancelreason", cancelReason);
      return `/Booking_Trip_ManagementService/Booking/CanceleBooking?${q.toString()}`;
    },
    ABSCOND_USER: (bookingmasterid: string | number) =>
      `/Booking_Trip_ManagementService/Booking/AbscondUser?bookingmasterid=${bookingmasterid}`,
    SAVE_PLAN_EXPIRY_DATE_EXTENSION:
      "/Booking_Trip_ManagementService/Booking/SavePlanExpiryDateExtension",
    GET_PLAN_EXPIRY_DATE_EXTENSIONS: (bookingmasterid: string | number) =>
      `/Booking_Trip_ManagementService/Booking/GetPlanExpiryDateExtensions?bookingmasterid=${bookingmasterid}`,
    EXPORT_BOOKING_DETAILS:
      "/Booking_Trip_ManagementService/Booking/ExportBookingDetails",
    GET_COST_BREAKDOWN_DIFFERENCE: (params: {
      oldplanid: number;
      newplanid: number;
      bookingmasterid: number;
      oldbookingplanid: number;
      onlyvehiclechange?: boolean;
    }) => {
      const only = params.onlyvehiclechange === true ? "true" : "false";
      return `/Booking_Trip_ManagementService/Booking/GetCostBreakdownDifference?oldplanid=${params.oldplanid}&newplanid=${params.newplanid}&bookingmasterid=${params.bookingmasterid}&oldbookingplanid=${params.oldbookingplanid}&onlyvehiclechange=${only}`;
    },
    GET_AUDIT_LOGS_BY_BOOKING_ID: (bookingId: string | number) =>
      `/Booking_Trip_ManagementService/Booking/GetAuditLogsByBookingId/${bookingId}`,
    GET_REFERRAL_SETTINGS:
      "/Booking_Trip_ManagementService/Booking/GetReferralSettings",
    SAVE_REFERRAL_SETTINGS:
      "/Booking_Trip_ManagementService/Booking/SaveReferralSettings",
    GET_REFUNDS: "/Booking_Trip_ManagementService/Booking/GetRefunds",
    INITIATE_REFUND_PROCESS: (bookingMasterId: string | number) =>
      `/Booking_Trip_ManagementService/Booking/InitiateRefundProcess/${bookingMasterId}`,
  },
  RIDES: {
    LIST: "/Booking_Trip_ManagementService/Booking/GetRideManagementData",
    DETAILS: (id: string) =>
      `/Booking_Trip_ManagementService/Trip/GetRideDetailsByBookingId?bookingmasterid=${id}`,
    GET_TRIP_BY_USER_ID: (userId: string | number) =>
      `/Booking_Trip_ManagementService/Trip/GetTripByUserId?userId=${userId}`,
    TELEMATICS:
      "/Booking_Trip_ManagementService/StatisticsData/GetTelematicsDeviceData",
    GET_VEHICLE_CONDITION_CHECKLIST:
      "/Booking_Trip_ManagementService/Trip/GetVehicleConditionCheckListAsync",
  },
  SUPPORT: {
    LIST: "/UserService/Support/GetAllSupportTicketDetails",
    DETAILS: (id: string) =>
      `/UserService/Support/GetSupportTicketDetailsById?ticketMasterId=${id}`,
    SAVE_NOTES: "/UserService/Support/SaveSupportTicketNotes",
    SAVE_TICKET_DETAILS: "/UserService/Support/SaveSupportTicketDetails",
    UPDATE_TICKET_DETAILS: "/UserService/Support/UpdateSupportTicketDetails",
  },
  DASHBOARD: {
    STATS: "/Dashboard/GetStats",
    GET_DASHBOARD_DATA: "/DashboardService/Dashboard/GetDashboardData",
  },
  SETTINGS: {
    SAVE_TERMS_PRIVACY_AND_AGREEMENTS:
      "/AdminService/SystemSettings/SaveTermsPrivacyAndAgreements",
    GET_PRIVACY_TERMS_TEMPLATE:
      "/AdminService/SystemSettings/GetPrivacy_TermsTemplate",
    GET_SYSTEM_SETTINGS_DATA:
      "/AdminService/SystemSettings/GetSystemSettingsData",
  },

  PROMO_CODES: {
    LIST: "/Booking_Trip_ManagementService/Booking/GetPromoCodes",
    BY_USER_ID: "/Booking_Trip_ManagementService/Booking/GetPromoCodesByUserId",
    CHECK_VALIDITY:
      "/Booking_Trip_ManagementService/Booking/CheckCouponValidity",
    CREATE: "/Booking_Trip_ManagementService/Booking/SavePromoCode",
    GET_BY_ID: (couponId: string) =>
      `/Booking_Trip_ManagementService/Booking/GetPromoCodeById/${couponId}`,
    DELETE: (couponId: string) =>
      `/Booking_Trip_ManagementService/Booking/DeletePromoCode/${couponId}`,
  },
  REFUNDS: {
    LIST: "/Booking_Trip_ManagementService/Booking/GetRefunds",
    INITIATE_REFUND_PROCESS: (bookingMasterId: string | number) =>
      `/Booking_Trip_ManagementService/Booking/InitiateRefundProcess/${bookingMasterId}`,
    ADD_REFUND: "/Booking_Trip_ManagementService/Booking/AddRefund",
    DELETE_REFUND: () => `/Booking_Trip_ManagementService/Booking/DeleteRefund`,
  },
};

// Navigation Items
export const NAVIGATION_ITEMS: NavigationItem[] = [
  {
    id: "dashboard",
    label: "Dashboard",
    path: "/dashboard",
    icon: "Dashboard",
  },
  {
    id: "user-management",
    label: "User Management",
    path: "/user-management",
    icon: "People",
  },
  {
    id: "hub-management",
    label: "Hub Management",
    path: "/hub-management",
    icon: "LocationOn",
  },
  {
    id: "staff-management",
    label: "Staff Management",
    path: "/staff-management",
    icon: "Groups",
  },
  {
    id: "vehicle-management",
    label: "Vehicle Management",
    path: "/vehicle-management",
    icon: "DirectionsCar",
  },
  {
    id: "plan-management",
    label: "Plan Management",
    path: "/plan-management",
    icon: "Event",
  },
  {
    id: "payment-management",
    label: "Payment Management",
    path: "/payment-management",
    icon: "Payment",
  },
  {
    id: "refund-management",
    label: "Refund Management",
    path: "/refund-management",
    icon: "CurrencyExchange",
  },
  {
    id: "booking-management",
    label: "Booking Management",
    path: "/booking-management",
    icon: "BookOnline",
  },
  {
    id: "ride-management",
    label: "Ride Management",
    path: "/ride-management",
    icon: "Ride",
  },
  {
    id: "support",
    label: "Support System",
    path: "/support",
    icon: "Support",
  },
  {
    id: "marketing",
    label: "Marketing",
    path: "/marketing/promo-codes",
    icon: "CardGiftcard",
    children: [
      {
        id: "promo-codes",
        label: "Promo Codes",
        path: "/marketing/promo-codes",
        icon: "CardGiftcard",
      },
      {
        id: "referral-settings",
        label: "Referral Settings",
        path: "/marketing/referral-settings",
        icon: "CardGiftcard",
      },
    ],
  },
  {
    id: "settings",
    label: "Setting",
    path: "/settings",
    icon: "Settings",
  },
];

// Theme Configuration
export const THEME_CONFIG = {
  PRIMARY_COLOR: "#1976d2",
  SECONDARY_COLOR: "#dc004e",
  SUCCESS_COLOR: "#2e7d32",
  WARNING_COLOR: "#ed6c02",
  ERROR_COLOR: "#d32f2f",
  INFO_COLOR: "#0288d1",
};

// Local Storage Keys
export const STORAGE_KEYS = {
  AUTH_TOKEN: "motovolt_auth_token",
  REFRESH_TOKEN: "motovolt_refresh_token",
  USER_DATA: "motovolt_user_data",
  REMEMBER_ME: "motovolt_remember_me",
};

// Pagination
export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 10,
  PAGE_SIZE_OPTIONS: [10, 25, 50, 100],
  MAX_PAGE_SIZE: 100,
};

// Form Validation
export const VALIDATION = {
  PASSWORD_MIN_LENGTH: 8,
  USERNAME_MIN_LENGTH: 3,
  PHONE_PATTERN: /^[6-9]\d{9}$/,
  EMAIL_PATTERN: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
};

// Status Options
export const STATUS_OPTIONS = [
  { value: "active", label: "Active", color: "success" },
  { value: "inactive", label: "Inactive", color: "warning" },
  { value: "banned", label: "Banned", color: "error" },
];

// User Role Options
export const USER_ROLE_OPTIONS = [
  { value: "admin", label: "Admin" },
  { value: "staff", label: "Staff" },
  { value: "user", label: "User" },
];

// ID Type Options
export const ID_TYPE_OPTIONS = [
  { id: 1, type: "Aadhaar Card" },
  { id: 2, type: "PAN Card" },
  { id: 3, type: "Driving License" },
  { id: 4, type: "Passport" },
  { id: 5, type: "Voter ID" },
];

// Vehicle Type Options
export const VEHICLE_TYPE_OPTIONS = [
  { value: "scooter", label: "Scooter" },
  { value: "bike", label: "Bike" },
  { value: "car", label: "Car" },
];

// Payment Method Options
export const PAYMENT_METHOD_OPTIONS = [
  { value: "card", label: "Credit/Debit Card" },
  { value: "upi", label: "UPI" },
  { value: "wallet", label: "Digital Wallet" },
  { value: "netbanking", label: "Net Banking" },
];

// Priority Options for Support Tickets
export const PRIORITY_OPTIONS = [
  { value: "low", label: "Low", color: "info" },
  { value: "medium", label: "Medium", color: "warning" },
  { value: "high", label: "High", color: "error" },
  { value: "urgent", label: "Urgent", color: "error" },
];

// Date Formats
export const DATE_FORMATS = {
  formatDate: (dateString: string): string => {
    if (!dateString) return "";
    const date = convertTimestampToIST(dateString);
    if (!date) return "";
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  },
  formatDateTime: (dateString: string): string => {
    if (!dateString) return "";
    try {
      const date = convertTimestampToIST(dateString);
      if (!date) return "";
      const month = date.toLocaleString("en-US", { month: "short" });
      const day = date.getDate();
      const year = date.getFullYear();
      const hours = date.getHours();
      const minutes = date.getMinutes();
      const ampm = hours >= 12 ? "PM" : "AM";
      const displayHours = (hours % 12 || 12).toString().padStart(2, "0");
      const displayMinutes = minutes.toString().padStart(2, "0");
      return `${month} ${day} ${year}, ${displayHours}:${displayMinutes} ${ampm}`;
    } catch {
      return "";
    }
  },
};

// Error Messages
export const ERROR_MESSAGES = {
  NETWORK_ERROR: "Network error. Please check your connection.",
  UNAUTHORIZED: "You are not authorized to perform this action.",
  FORBIDDEN: "Access denied.",
  NOT_FOUND: "The requested resource was not found.",
  SERVER_ERROR: "Internal server error. Please try again later.",
  VALIDATION_ERROR: "Please check your input and try again.",
  LOGIN_REQUIRED: "Please login to continue.",
  SESSION_EXPIRED: "Your session has expired. Please login again.",
};

// Success Messages
export const SUCCESS_MESSAGES = {
  LOGIN_SUCCESS: "Login successful!",
  LOGOUT_SUCCESS: "Logout successful!",
  SAVE_SUCCESS: "Data saved successfully!",
  DELETE_SUCCESS: "Data deleted successfully!",
  UPDATE_SUCCESS: "Data updated successfully!",
  CREATE_SUCCESS: "Data created successfully!",
};

// File Upload Configuration
export const FILE_UPLOAD_CONFIG = {
  FOLDER_PATHS: {
    STAFF: "staff",
    USERS: "users",
    VEHICLES: "vehicles",
    HUBS: "hub",
  },
  FOLDER_TYPES: {
    PROFILE: "profile",
    ID_PROOF: "id-proof",
    DOCUMENT: "document",
    HUBS: "hub",
    VEHICLE_IMAGE: "vehicle-image",
  },
  ALLOWED_IMAGE_TYPES: [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/gif",
    "image/webp",
  ],
  ALLOWED_VIDEO_TYPES: ["video/mp4", "video/webm", "video/ogg"],
  // Compression preferences (client-side best-effort)
  IMAGE_TARGET_MAX_BYTES: 250 * 1024, // ~250KB
  IMAGE_MAX_DIMENSION: 1600, // downscale longest edge to this if needed
  IMAGE_QUALITY_STEP: 0.06, // reduce quality in steps while targeting bytes
};
